import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { CartAWB, ErrorInputFieldComponent, PromoCode, PromoCodeListResponse } from '@cargos/sprintpay-models';
import { finalize, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AwbPromoCode, PromoCodesService } from 'src/app/shared/services/promo-code.service';
import { ErrorHandler } from '../../shared/services/error-handler.abstract';
import { PromoCodeFormErrorJSON, PromoCodeFormGroupType } from './models/promo-code-form';
import { PromoCodeList } from './models/promo-code-list-input';
import { formPromoCodeErrors } from './utils/form-errors';

@Component({
    selector: 'app-promo-code',
    templateUrl: './promo-code.component.html',
    styleUrls: ['./promo-code.component.scss'],
    standalone: false,
})
export class PromoCodeComponent implements OnInit, OnDestroy {
    public promoCodeForm!: PromoCodeFormGroupType;
    public isLoading = false;
    public promoCodeApplied: string | undefined = '';
    public messageConfig!: ErrorInputFieldComponent[];
    public promoCodesList: PromoCodeList[] = [];
    private unsubscribe$ = new Subject<void>();
    public formErrors: PromoCodeFormErrorJSON;

    @Input() awbNumber: string = '';
    @Input() awbList: CartAWB[] = [];
    @Output() eventOnOpenDropDown = new EventEmitter<unknown>();
    @Output() eventOnCloseDropDown = new EventEmitter<unknown>();

    constructor(
        private formBuilder: NonNullableFormBuilder,
        private promoCodeService: PromoCodesService,
        private errorHandler: ErrorHandler
    ) {
        this.formErrors = formPromoCodeErrors;
    }

    ngOnInit(): void {
        this.getPromoCodes();
        this.createForm();
        this.subscribePromoCodesApplied();
    }

    get promoCode(): string {
        return this.promoCodeForm?.get('promoCode')?.value || '';
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createForm(): void {
        this.promoCodeForm = this.formBuilder.group({
            promoCode: ['', [Validators.required]],
        });
    }

    deletePromoCode(): void {
        this.promoCodeService.removePromoCode(this.awbNumber, this.promoCode);
    }

    subscribePromoCodesApplied(): void {
        this.getPromoCodesApplied()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (arrayPromoCode) => {
                    this.promoCodeApplied = arrayPromoCode.find(
                        (promocodeApplied) => promocodeApplied.awb === this.awbNumber
                    )?.promoCodeId;

                    this.promoCodeForm.patchValue({
                        promoCode: this.promoCodeApplied,
                    });
                },
            });
    }

    validatePromoCode(): void {
        if (!this.promoCodeForm.valid) {
            return;
        }

        if (this.promoCodeService.findPromoCode(this.promoCode)) {
            this.setPromoCodeError('The promo code has already been used');
            return;
        }

        this.isLoading = true;

        this.promoCodeService
            .validatePromoCode(this.promoCode)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (response: PromoCode) => {
                    if (!response.isValid) {
                        this.setPromoCodeError(response.message || '');
                        return;
                    }
                    this.promoCodeService.setPromoCodesApplied(
                        this.awbNumber,
                        response.promoCodeId || '',
                        response.awb
                    );
                },
                error: (error) => {
                    const errorMsg = this.errorHandler.getErrorMsg(error);
                    this.setPromoCodeError(errorMsg);
                },
            });
    }

    get errorVisible(): boolean {
        if (!this.promoCodeForm?.get('promoCode')?.errors) {
            return false;
        }

        return this.promoCodeForm.touched || this.promoCodeForm.dirty;
    }

    setPromoCodeError(message: string): void {
        const messageList = [
            {
                code: 'invalid',
                message: message,
            },
        ];
        this.messageConfig = Array.isArray(messageList) ? messageList : [messageList];
        this.promoCodeForm.get('promoCode')?.setErrors({ invalid: true });
    }

    getPromoCodes(): void {
        this.isLoading = true;
        this.promoCodeService
            .getPromoCodesByUser$()
            .pipe(
                map((promoCodesList: PromoCodeListResponse[]) => {
                    return promoCodesList.map((promoCodeValue) => ({
                        name: promoCodeValue.promoCodeId,
                    }));
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (promoCodeList: { name: string }[]) => {
                    this.isLoading = false;
                    this.promoCodesList = promoCodeList;
                },
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    onOpenDropDown(): void {
        this.eventOnOpenDropDown.emit();
    }

    onCloseDropDown(): void {
        this.eventOnCloseDropDown.emit();
    }

    getPromoCodesApplied(): Observable<AwbPromoCode[]> {
        return this.promoCodeService.getAllPromoCodeApplied$();
    }
}
